import React, { useState, useEffect, useRef } from "react";
import {
  FaBrain,
  FaChartLine,
  FaUserFriends,
  FaGamepad,
  FaClock,
  FaFileAlt,
  FaTimes,
  FaArrowRight,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";

const featuresData = [
  {
    icon: <FaBrain size={30} className="text-white" />,
    title: "AI-Powered Learning",
    description:
      "Get personalized quiz suggestions and insights based on your learning pace and performance using intelligent algorithms.",
    detailedContent: `
      <h3>How AI Enhances Your Learning</h3>
      <p>Our advanced machine learning algorithms analyze your performance patterns, identifying strengths and areas for improvement.</p>
      <ul>
        <li>Personalized question difficulty that adapts as you learn</li>
        <li>Smart content recommendations based on your learning style</li>
        <li>Predictive analytics to optimize your study schedule</li>
      </ul>
      <p>The more you use QUZY, the smarter it gets - creating a truly personalized learning experience that evolves with you.</p>
    `
  },
  {
    icon: <FaChartLine size={30} className="text-white" />,
    title: "Progress Tracking",
    description:
      "Visualize your growth with detailed analytics that track your accuracy, speed, and subject-wise performance over time.",
    detailedContent: `
      <h3>Data-Driven Learning Insights</h3>
      <p>Comprehensive dashboards and visual reports help you understand your learning journey at a glance.</p>
      <ul>
        <li>Interactive performance graphs showing improvement over time</li>
        <li>Subject and topic breakdown to identify knowledge gaps</li>
        <li>Comparative analytics to benchmark against peers</li>
        <li>Exportable reports to share with teachers or mentors</li>
      </ul>
      <p>Transform your study habits with actionable insights that guide your learning strategy.</p>
    `
  },
  {
    icon: <FaUserFriends size={30} className="text-white" />,
    title: "Peer Collaboration",
    description:
      "Connect with friends, challenge peers in quiz battles, and learn better together with a thriving student community.",
    detailedContent: `
      <h3>Learning is Better Together</h3>
      <p>Our social learning features turn studying from a solitary activity into an engaging, collaborative experience.</p>
      <ul>
        <li>Create study groups with shared resources and discussions</li>
        <li>Challenge friends to knowledge battles on any subject</li>
        <li>Participate in community-wide competitions and leaderboards</li>
        <li>Exchange study materials and quiz questions with peers</li>
      </ul>
      <p>Research shows collaborative learning improves retention by up to 50% - experience the difference with QUZY's social features.</p>
    `
  },
  {
    icon: <FaGamepad size={30} className="text-white" />,
    title: "Gamified Quizzes",
    description:
      "Earn points, unlock badges, and level up as you learn – because studying should be as fun as it is effective.",
    detailedContent: `
      <h3>Turn Learning into an Adventure</h3>
      <p>Our gamification elements are designed by educational psychologists to maximize engagement while ensuring effective learning.</p>
      <ul>
        <li>Earn XP and level up your student profile across different subjects</li>
        <li>Unlock achievement badges that showcase your academic prowess</li>
        <li>Complete daily challenges for bonus rewards and streaks</li>
        <li>Participate in themed seasonal events with special content</li>
      </ul>
      <p>QUZY's game mechanics trigger dopamine release associated with reward systems, making learning addictively fun while improving information retention.</p>
    `
  },
  {
    icon: <FaClock size={30} className="text-white" />,
    title: "Smart Scheduling",
    description:
      "Access your digital library and quizzes anytime, with AI-driven scheduling and daily reminders to enhance your learning consistency.",
    detailedContent: `
      <h3>Optimize Your Study Time</h3>
      <p>Our intelligent scheduling system works with your natural learning rhythms and availability to maximize productivity.</p>
      <ul>
        <li>AI-powered study plans based on your learning goals and deadlines</li>
        <li>Spaced repetition algorithms that optimize review timing</li>
        <li>Calendar integration with customizable notifications</li>
        <li>Focus mode timers with break reminders for optimal concentration</li>
      </ul>
      <p>QUZY's scheduling features are built on cognitive science principles that ensure you're studying the right material at the right time for maximum retention.</p>
    `
  },
  {
    icon: <FaFileAlt size={30} className="text-white" />,
    title: "Topic-Wise Resources",
    description:
      "Access curated study material and questions categorized by subject and topic to help you focus on what matters.",
    detailedContent: `
      <h3>Comprehensive Learning Library</h3>
      <p>Our extensive database of educational resources is organized to make finding relevant content quick and intuitive.</p>
      <ul>
        <li>Expertly curated content aligned with major curricula worldwide</li>
        <li>Multi-format resources including text, video, and interactive elements</li>
        <li>Difficulty levels from beginner to advanced for progressive learning</li>
        <li>Regular updates with fresh content and the latest educational standards</li>
      </ul>
      <p>With QUZY's topic-wise organization, you'll always have the right resources at your fingertips, allowing you to focus on learning rather than searching.</p>
    `
  },
];

const FeatureDetail = ({ feature, isMobile }) => {
  return (
    <div className={`bg-gradient-to-br from-white to-indigo-50 rounded-3xl shadow-2xl ${isMobile ? 'p-4' : 'p-8'} h-full overflow-hidden relative`}>
      {/* Background decoration */}
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-indigo-100 rounded-full opacity-50"></div>
      <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-indigo-100 rounded-full opacity-50"></div>
      
      <div className="relative z-10">
        {/* Header with icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center p-2 bg-indigo-600 rounded-2xl shadow-lg mb-4 transform -rotate-3">
            <div className="bg-indigo-500 w-16 h-16 flex items-center justify-center rounded-xl">
              {feature.icon}
            </div>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-2">{feature.title}</h3>
          <div className="w-20 h-1 bg-indigo-500 rounded-full mb-4"></div>
          <p className="text-gray-600 text-lg">{feature.description}</p>
        </div>
        
        {/* Content */}
        <div 
          className="prose prose-lg max-w-none prose-indigo"
          dangerouslySetInnerHTML={{ __html: feature.detailedContent }}
        />
        
        {/* Feature highlights */}
        <div className={`mt-8 bg-white bg-opacity-70 backdrop-blur-sm ${isMobile ? 'p-4' : 'p-6'} rounded-2xl shadow-md border border-indigo-100`}>
          <h4 className="text-xl font-semibold text-indigo-700 mb-4">Why students love this feature</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
              <span>Improves learning outcomes by 43%</span>
            </div>
            <div className="flex items-start">
              <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
              <span>Reduces study time while increasing retention</span>
            </div>
            <div className="flex items-start">
              <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
              <span>Makes learning more enjoyable and engaging</span>
            </div>
            <div className="flex items-start">
              <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
              <span>Adapts to your personal learning style</span>
            </div>
          </div>
        </div>
        
        {/* CTA */}
        <div className="mt-8 flex justify-end">
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-8 rounded-full transition-all duration-300 font-medium flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-1">
            Try it now <FaArrowRight className="ml-2" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const Feature = () => {
  const [selectedFeature, setSelectedFeature] = useState(featuresData[0]);
  const [expandedFeatureIndices, setExpandedFeatureIndices] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);

  // Check if we're on mobile on component mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleFeatureClick = (feature, index, event) => {
    // Prevent default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation(); // Stop event propagation
    }
    
    // Store current scroll position
    const scrollPosition = window.scrollY;
    
    // On desktop, just update the selected feature for the right panel
    setSelectedFeature(feature);
    
    // On mobile/tablet, toggle the expanded state for the clicked feature
    // without closing other expanded features
    if (isMobile) {
      setExpandedFeatureIndices(prev => {
        // If already expanded, remove it from the array
        if (prev.includes(index)) {
          return prev.filter(i => i !== index);
        }
        // Otherwise add it to the array
        return [...prev, index];
      });
      
      // Restore scroll position after state update
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 10);
    }
  };

  // Check if a feature is expanded
  const isFeatureExpanded = (index) => {
    return expandedFeatureIndices.includes(index);
  };

  return (
    <section id="feature" className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="text-center mb-16">
        <h2 className="text-6xl font-bold text-gray-200 uppercase opacity-30 mb-2">
          Features
        </h2>
        <h3 className="text-4xl md:text-5xl font-bold -mt-8 relative z-10 bg-gradient-to-r from-indigo-600 to-purple-500 bg-clip-text text-transparent">
          FEATURES
        </h3>
        <p className="text-lg text-gray-600 mt-2 px-4 max-w-2xl mx-auto">
          Explore the tools and technology that make QUZY the ultimate quiz-based learning platform.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4" ref={containerRef}>
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
          {/* Features List - Left Side (30%) */}
          <div className="lg:col-span-3 bg-white rounded-3xl shadow-xl p-6 h-fit">
            <h4 className="text-lg font-semibold text-gray-700 mb-6 pl-4 border-l-4 border-indigo-500 lg:block hidden">Discover Our Features</h4>
            
            {/* This wrapper ensures consistent height */}
            <div className="relative">
              <div className="space-y-4">
                {featuresData.map((feature, index) => (
                  <div 
                    key={index} 
                    className="transition-all duration-300"
                  >
                    {/* Feature Tab */}
                    <div
                      className={`p-4 ${isFeatureExpanded(index) ? 'rounded-t-2xl' : 'rounded-2xl'} cursor-pointer transition-all duration-300 flex items-center ${selectedFeature.title === feature.title && !isMobile 
                        ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg' 
                        : isFeatureExpanded(index) 
                          ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg rounded-b-none' 
                          : 'bg-white hover:bg-gray-50 border border-gray-100'}`}
                      onClick={(e) => handleFeatureClick(feature, index, e)}
                    >
                      <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${(selectedFeature.title === feature.title && !isMobile) || isFeatureExpanded(index) ? 'bg-white bg-opacity-20' : 'bg-indigo-100'} mr-4`}>
                        {(selectedFeature.title === feature.title && !isMobile) || isFeatureExpanded(index)
                          ? React.cloneElement(feature.icon, { className: 'text-white' })
                          : React.cloneElement(feature.icon, { className: 'text-indigo-500' })}
                      </div>
                      <div className="flex-grow">
                        <h4 className={`font-bold ${(selectedFeature.title === feature.title && !isMobile) || isFeatureExpanded(index) ? 'text-white' : 'text-gray-800'}`}>{feature.title}</h4>
                        <p className={`text-sm line-clamp-1 ${(selectedFeature.title === feature.title && !isMobile) || isFeatureExpanded(index) ? 'text-indigo-100' : 'text-gray-500'}`}>{feature.description}</p>
                      </div>
                      {/* Show chevron only on mobile/tablet */}
                      <div className="lg:hidden">
                        {isFeatureExpanded(index) ? (
                          <FaChevronUp className="text-white ml-2" />
                        ) : (
                          <FaChevronDown className="text-gray-400 ml-2" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expandable Content - Only visible on mobile/tablet when expanded */}
                    {isFeatureExpanded(index) && (
                      <div className="lg:hidden bg-white border-l border-r border-b border-gray-100 rounded-b-2xl shadow-lg overflow-hidden transition-all duration-300">
                        <div className="px-0">
                          <FeatureDetail feature={feature} isMobile={true} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Feature Details - Right Side (70%) - Only visible on desktop */}
          <div className="hidden lg:block lg:col-span-7">
            <FeatureDetail feature={selectedFeature} isMobile={false} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Feature;
